import React from 'react';
import { Box, TableCell } from '@mui/material';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';

import type { SortDirection } from '../types';
import { FONT } from '../constants';

export const SortIcon: React.FC<{ active: boolean; direction: SortDirection }> = ({
  active,
  direction,
}) => {
  if (!active) return <ChevronsUpDown size={12} style={{ opacity: 0.45 }} />;
  return direction === 'ASC' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
};

interface SortControlProps {
  label: string;
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
}

// Plain (non-sortable) header cell.
export const PlainHeaderCell: React.FC<{ label: string; align?: 'left' | 'right' }> = ({
  label,
  align,
}) => (
  <TableCell
    align={align}
    sx={{
      fontSize: FONT.sm,
      fontWeight: 700,
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    }}
  >
    {label}
  </TableCell>
);

// Sortable header cell → toggles server-side sort for its column.
export const SortableHeaderCell: React.FC<SortControlProps> = ({
  label,
  active,
  direction,
  onClick,
}) => (
  <TableCell
    onClick={onClick}
    sx={{
      fontSize: FONT.sm,
      fontWeight: 700,
      color: active ? '#0f172a' : '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      cursor: 'pointer',
      userSelect: 'none',
      '&:hover': { color: '#0f172a' },
    }}
  >
    <Box component="span" display="inline-flex" alignItems="center" gap="4px">
      {label}
      <SortIcon active={active} direction={direction} />
    </Box>
  </TableCell>
);

// Compact sort pill embedded inside a composite header (e.g. the Artist / Match cell).
export const MiniSortPill: React.FC<SortControlProps> = ({ label, active, direction, onClick }) => (
  <Box
    component="span"
    onClick={onClick}
    display="inline-flex"
    alignItems="center"
    gap="2px"
    sx={{
      cursor: 'pointer',
      fontSize: FONT.sm,
      fontWeight: 500,
      color: active ? '#0f172a' : '#94a3b8',
      padding: '1px 5px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      userSelect: 'none',
      '&:hover': { color: '#0f172a' },
    }}
  >
    {label}
    <SortIcon active={active} direction={direction} />
  </Box>
);
